import React, {
  cloneElement,
  memo,
  Children,
  ReactNode,
  ReactElement,
  Dispatch,
  SetStateAction,
  ElementType,
} from 'react'

import { includes, length, reject, equals } from 'ramda'

import { useHandler } from '@surglogs-app/hooks'
import { FlexBox } from '@surglogs-app/ui'

interface CheckboxGroupProps {
  onChange?: Dispatch<SetStateAction<number[]>>
  onBlur?: () => void
  disabled?: boolean
  disableEmpty?: boolean
  single?: boolean
  selectedValues?: number[]
  children: ReactElement[]
  name: string
  disabledValues?: number[]
  error?: string
  wrapperElement?: ElementType
  'aria-label'?: string
  warning?: string
}

const CheckboxGroupDefaultWrapper = ({
  children,
  'aria-label': ariaLabel,
}: {
  children: ReactNode
  'aria-label': string
}) => {
  return (
    <FlexBox
      {...{
        role: 'radiogroup',
        'aria-label': ariaLabel,
        fd: 'column',
        gap: 'sm',
      }}>
      {children}
    </FlexBox>
  )
}

export const CheckboxGroup = memo(
  ({
    onChange,
    onBlur,
    disabled,
    disableEmpty,
    single,
    selectedValues = [],
    children,
    name,
    disabledValues,
    error,
    wrapperElement: WrapperElement = CheckboxGroupDefaultWrapper,
    'aria-label': ariaLabel,
    warning,
  }: CheckboxGroupProps) => {
    const createOnChangeHandler = useHandler((value) => () => {
      if (disabled) {
        return
      }

      const valueIsBeingSelected = !includes(value, selectedValues)
      const newSelectedValues = valueIsBeingSelected
        ? [...(single === true ? [] : selectedValues), value]
        : disableEmpty === true && length(selectedValues) === 1
        ? selectedValues
        : reject(equals(value), selectedValues)

      onChange?.(newSelectedValues)
      onBlur?.()
    })

    return (
      <WrapperElement {...{ role: 'radiogroup', 'aria-label': ariaLabel }}>
        {Children.map(children, (child: ReactElement<any>) => {
          if (!child) {
            return child
          }

          const { value } = child.props
          const checked = includes(value, selectedValues)
          const isDisabled = Array.isArray(disabledValues)
            ? includes(value, disabledValues)
            : false

          const newProps = {
            name,
            checked,
            error,
            disabled: isDisabled,
            ...(Array.isArray(disabledValues) ? { disabled } : {}),
            ...(!disabled ? { onChange: createOnChangeHandler(value) } : {}),
            warning,
          }

          return cloneElement(child, newProps)
        })}
      </WrapperElement>
    )
  },
)
